import { DropdownOption } from '../components/data-set/data-set.component';
import { formatDate } from '@angular/common';

export type ContractSettingsFields = Components.Schemas.ContractSettingsFields;

export const emptyContractSettingsFields = (): ContractSettingsFields => ({
	company: {
		id: '',
		name: '',
		active: false,
	},
	netsuiteCustomerInternalId: '',
	contractName: '',
	contractId: '',
	unitOfMeassureTypeId: '',
	currencyTypeId: '',
	unitPrice: 0,
	billingStartDate: '',
	billingTriggerTypeId: '',
	duration: '',
	annualIncreasePercent: 0,
	areDevicesLeased: false,
	isMonthlyAutoRenewed: false,
	addOnBillingSettings: [],
});

export const convertWebBillingContractToApiFormat = (
	savedBillingContract: BillingContract
): ContractSettingsFields => {
	const apiBillingForm = emptyContractSettingsFields();
	apiBillingForm.netsuiteCustomerInternalId =
		savedBillingContract?.netsuiteCustomerInternalId ?? '';
	apiBillingForm.company = {
		id: savedBillingContract?.company.id ?? '',
		name: savedBillingContract?.company.name ?? '',
		active: savedBillingContract?.company.active ?? false,
	};
	apiBillingForm.contractName = savedBillingContract?.contractName ?? '';
	apiBillingForm.contractId = savedBillingContract?.contractId ?? '';
	apiBillingForm.unitOfMeassureTypeId =
		savedBillingContract?.unitOfMeassureTypeId?.toString() ?? '';
	apiBillingForm.currencyTypeId =
		savedBillingContract?.currencyTypeId.toString() ?? '';
	apiBillingForm.unitPrice = savedBillingContract?.unitPrice ?? 0;
	apiBillingForm.billingStartDate = formatDate(
		savedBillingContract?.billingStartDate!,
		'yyyy-MM-dd',
		'en-US'
	);
	apiBillingForm.billingTriggerTypeId =
		savedBillingContract?.billingTriggerTypeId.toString() ?? '';
	apiBillingForm.duration = savedBillingContract?.duration.toString() ?? '';
	apiBillingForm.annualIncreasePercent =
		savedBillingContract?.annualIncreasePercent
			? savedBillingContract?.annualIncreasePercent / 100
			: 0;
	apiBillingForm.areDevicesLeased =
		savedBillingContract?.areDevicesLeased ?? false;
	apiBillingForm.isMonthlyAutoRenewed =
		savedBillingContract?.isMonthlyAutoRenewed ?? false;
	apiBillingForm.addOnBillingSettings =
		savedBillingContract?.addOnBilling.map((addOn) => ({
			description: addOn.description,
			unitPrice: addOn.unitPrice,
			quantity: addOn.quantity.toString(),
			recurringChargeTypeId: addOn.recurringChargeTypeId.toString(),
		})) ?? [];
	return apiBillingForm;
};

export interface BillingContract {
	id?: string;
	company: Company;
	netsuiteCustomerInternalId: string;
	contractName: string;
	contractId: string;
	unitOfMeassureTypeId: string;
	currencyTypeId: string;
	unitPrice: number;
	billingStartDate: Date;
	billingTriggerTypeId: string;
	duration: number;
	annualIncreasePercent?: number;
	areDevicesLeased: boolean;
	isMonthlyAutoRenewed: boolean;
	status?: ContractStatus;
	addOnBilling: AddOnBilling[];
}

interface Company {
	id: string;
	name: string;
	active: boolean;
}

export interface AddOnBilling {
	description: string;
	quantity: number;
	unitPrice: number;
	recurringChargeTypeId: string;
}

export const currencyTypeOptions = [
	{
		key: '1',
		value: $localize`:@@billingContract-dollarsOptionLabel:$ US Dollars (USD)`,
	},
	{
		key: '2',
		value: $localize`:@@billingContract-poundOptionLabel:£ Pound Sterling (GBP)`,
	},
	{
		key: '3',
		value: $localize`:@@billingContract-euroOptionLabel:€ Euro (EUR)`,
	},
];

export const mrcOrNrcOptions = [
	{
		key: '1',
		value: $localize`:@@billingContract-nrcOptionLabel:NRC`,
	},
	{
		key: '2',
		value: $localize`:@@billingContract-mrcOptionLabel:MRC`,
	},
];

export const unitsOfMeasureOptions = [
	{
		key: '1',
		value: $localize`:@@billingContract-devicesOptionLabel:Devices`,
	},
];

export const billingTriggerOptions = [
	{
		key: '1',
		value: $localize`:@@billingContract-devicesInServiceOptionLabel:Devices In-Service`,
	},
	{
		key: '2',
		value: $localize`:@@billingContract-allDevicesOptionLabel:All Devices`,
	},
];

// Radio button options
export const billingEndDateOptions = [
	{
		key: false,
		valueTitle: $localize`:@@billingContract-doNotAutoRenewTitle:Do not auto-renew`,
		valueDesc: $localize`:@@billingContract-doNotAutoRenewDesc:Do not generate a new invoice and suspend all accounts, terminating access to the system.`,
		checked: true,
	},
	{
		key: true,
		valueTitle: $localize`:@@billingContract-autoRenewTitle:Auto-renew`,
		valueTitleSmallerText: $localize`:@@billingContract-autoRenewTitleSmallerText:(month to month)`,
		valueDesc: $localize`:@@billingContract-autoRenewDesc:If not manually cancelled, the billing automatically ends after 12 months. Any annual increase will be applied.`,
		checked: false,
	},
];

export const billingLabels = {
	companyName: $localize`:@@billingContract-companyNameLabel:Company Name`,
	netsuiteCustomerInternalId: $localize`:@@billingContract-netsuiteCustomerInternalId:Netsuite Customer Internal ID`,
	contractName: $localize`:@@billingContract-contractNameLabel:Contract Name`,
	contractId: $localize`:@@billingContract-contractIdLabel:Contract ID`,
	unitOfMeasure: $localize`:@@billingContract-unitOfMeasureLabel:Units of Measure`,
	currencyType: $localize`:@@billingContract-currencyTypeLabel:Currency Type`,
	unitPrice: $localize`:@@billingContract-unitPriceLabel:Unit Price`,
	billingStartDate: $localize`:@@billingContract-billingStartDateLabel:Billing Start Date`,
	billingTrigger: $localize`:@@billingContract-billingTriggerLabel:Billing Trigger`,
	durationInMonths: $localize`:@@billingContract-durationInMonthsLabel:Duration (Months)`,
	adjustmentRate: $localize`:@@billingContract-adjustmentRateLabel:Adjustment Rate`,
	status: $localize`:@@billingContract-status:Status`,
	annualIncrease: $localize`:@@billingContract-annualIncrease:Annual Increase`,
	deviceLease: $localize`:@@billingContract-deviceLease:Device Lease`,
	billingEndDate: $localize`:@@billingContract-billingEndDate:Billing End Date`,
	yes: $localize`:@@billingContract-yes:Yes`,
	no: $localize`:@@billingContract-no:No`,
};

export const contractStatusDropdownOptions: DropdownOption[] = [
	{ label: $localize`:@@billingListTable-active:Active`, value: 'active' },
	{
		label: $localize`:@@billingListTable-upcoming:Upcoming`,
		value: 'upcoming',
	},
	{
		label: $localize`:@@billingListTable-manuallyEnded:Manually Ended`,
		value: 'manuallyEnded',
	},
	{
		label: $localize`:@@billingListTable-completed:Completed`,
		value: 'completed',
	},
	{
		label: $localize`:@@billingListTable-autoRenewed:Auto Renewed`,
		value: 'autoRenewed',
	},
];

export type ContractStatus =
	'active' | 'upcoming' | 'manuallyEnded' | 'completed' | 'autoRenewed';
